import { useMemo, useSyncExternalStore } from 'react';

/**
 * Custom hook to get the search parameters from the current URL in a Next.js application.
 *
 * @returns {URLSearchParams} - The search parameters of the current URL.
 *
 * Example usage:
 * const searchParams = useNextSearchParams();
 */
export function useNextSearchParams(): URLSearchParams {
  const subscribe = (onStoreChange: () => void) => {
    if (typeof window === 'undefined') return () => {};
    const handler = () => onStoreChange();
    window.addEventListener('popstate', handler);
    window.addEventListener('app:locationchange', handler);
    return () => {
      window.removeEventListener('popstate', handler);
      window.removeEventListener('app:locationchange', handler);
    };
  };

  const getSnapshot = () => {
    if (typeof window === 'undefined') return '';
    return window.location.search;
  };

  const search = useSyncExternalStore(subscribe, getSnapshot, () => '');

  const searchParams: URLSearchParams = useMemo(() => {
    return new URLSearchParams(search);
  }, [search]);

  return searchParams;
}
